<template>
  <Teleport to="body">
    <Transition name="modal" appear>
      <div class="fixed inset-0 z-50 flex items-center justify-center p-4" @click.self="$emit('close')">
        <div class="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>

        <div class="relative w-full max-w-3xl bg-white dark:bg-[#1C2128] rounded-2xl shadow-2xl overflow-hidden animate-in flex flex-col md:flex-row max-h-[90vh]">

          <!-- Left: informational panel, animated gradient -->
          <div class="gradient-panel relative overflow-hidden flex-shrink-0 md:w-[38%] p-7 sm:p-8 flex flex-col justify-between text-white">
            <div class="blob blob-1"></div>
            <div class="blob blob-2"></div>

            <div class="relative z-10">
              <div class="w-12 h-12 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center mb-5 ring-1 ring-white/20">
                <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z"/>
                </svg>
              </div>
              <h3 class="text-lg font-bold leading-snug mb-2.5">About applications</h3>
              <p class="text-sm text-blue-50/90 leading-relaxed">
                Applications track the software your organization builds or relies on, from idea to retirement.
              </p>
            </div>

            <ul class="relative z-10 space-y-3 mt-8">
              <li class="flex items-start gap-2.5">
                <svg class="w-4 h-4 text-blue-100 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/>
                </svg>
                <span class="text-xs text-blue-50/90 leading-relaxed">Belongs directly to your organization</span>
              </li>
              <li class="flex items-start gap-2.5">
                <svg class="w-4 h-4 text-blue-100 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/>
                </svg>
                <span class="text-xs text-blue-50/90 leading-relaxed">Criticality signals how much it matters</span>
              </li>
              <li class="flex items-start gap-2.5">
                <svg class="w-4 h-4 text-blue-100 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/>
                </svg>
                <span class="text-xs text-blue-50/90 leading-relaxed">Status tracks its lifecycle phase</span>
              </li>
            </ul>
          </div>

          <!-- Right: form -->
          <div class="flex-1 flex flex-col min-w-0 overflow-y-auto">
            <div class="flex items-center justify-between px-6 sm:px-7 pt-6 pb-4">
              <h3 class="text-base font-semibold text-gray-900 dark:text-white">{{ title }}</h3>
              <button @click="$emit('close')" type="button"
                class="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <div class="px-6 sm:px-7 space-y-4 flex-1">
              <div class="relative name-suggestions">
                <div class="flex items-center justify-between mb-1.5">
                  <label class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Name<span class="text-red-500">*</span></label>
                  <span class="text-[11px] tabular-nums" :class="name.length >= NAME_MAX_LENGTH ? 'text-red-500 dark:text-red-400' : 'text-gray-400 dark:text-gray-500'">{{ name.length }}/{{ NAME_MAX_LENGTH }}</span>
                </div>
                <div class="relative">
                  <div :class="[error ? 'border-red-400 dark:border-red-500' : 'border-gray-200 dark:border-gray-700',
                               showSuggestions && suggestions.length ? 'rounded-t-lg border-b-transparent bg-gray-50 dark:bg-white/[0.04]' : 'rounded-lg bg-white dark:bg-[#0d1117]']"
                    class="w-full h-[42px] flex items-center gap-2 pl-3.5 pr-3 border name-input-row">
                    <ItComponentLogo v-if="matchedApplication" :name="matchedApplication.name" :logo-url="matchedApplication.logo_url"
                      size="w-5 h-5" initial-text-size="text-[9px]" class="flex-shrink-0" />
                    <input ref="nameInput" v-model="name" type="text" placeholder="e.g. Booking Portal" autocomplete="off" :maxlength="NAME_MAX_LENGTH"
                      @keydown="handleNameKeydown" @focus="showSuggestions = true" @input="showSuggestions = true"
                      class="flex-1 min-w-0 text-sm bg-transparent border-0 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 placeholder:text-xs focus:outline-none focus:ring-0" />
                    <svg class="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0 transition-transform"
                      :class="showSuggestions && suggestions.length ? 'rotate-180' : ''" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5"/>
                    </svg>
                  </div>

                  <div v-if="showSuggestions && suggestions.length"
                    class="absolute z-30 top-full left-0 w-full bg-white dark:bg-[#1C2128] border border-t-0 border-gray-200 dark:border-gray-700 rounded-b-lg shadow-lg py-1.5 max-h-56 overflow-y-auto">
                    <button v-for="(suggestion, index) in suggestions" :key="suggestion.name" type="button"
                      :ref="(el) => setSuggestionRef(el, index)"
                      @click="selectSuggestion(suggestion)" @mouseenter="activeSuggestionIndex = index"
                      :class="index === activeSuggestionIndex ? 'bg-gray-50 dark:bg-gray-800' : ''"
                      class="w-full flex items-center gap-2.5 text-left px-3.5 py-2 transition-colors">
                      <ItComponentLogo :name="suggestion.name" :logo-url="suggestion.logo_url" size="w-7 h-7" initial-text-size="text-[10px]" />
                      <div class="min-w-0 flex-1">
                        <p class="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{{ suggestion.name }}</p>
                        <p class="text-xs text-gray-400 dark:text-gray-500 truncate">{{ suggestion.description }}</p>
                      </div>
                    </button>
                  </div>
                </div>
                <p v-if="error" class="mt-1.5 text-xs text-red-500 dark:text-red-400 font-medium">{{ error }}</p>
                <p v-else class="mt-1.5 text-xs text-gray-400 dark:text-gray-500">Search the shared catalog, or type a new name.</p>
              </div>

              <div>
                <div class="flex items-center justify-between mb-1.5">
                  <label class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Description <span class="text-gray-400 dark:text-gray-500 font-normal normal-case">(optional)</span></label>
                  <span class="text-[11px] tabular-nums" :class="description.length >= DESCRIPTION_MAX_LENGTH ? 'text-red-500 dark:text-red-400' : 'text-gray-400 dark:text-gray-500'">{{ description.length }}/{{ DESCRIPTION_MAX_LENGTH }}</span>
                </div>
                <textarea v-model="description" rows="3" placeholder="Optional" :maxlength="DESCRIPTION_MAX_LENGTH"
                  class="w-full px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none leading-relaxed"></textarea>
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <StatusField v-model="status" />
                <CriticalityField v-model="criticality" />
              </div>
            </div>

            <div class="flex gap-3 px-6 sm:px-7 py-5 mt-2">
              <button type="button" @click="$emit('close')"
                class="flex-1 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors">
                Cancel
              </button>
              <button type="button" @click="submit" :disabled="saving"
                class="flex-1 inline-flex items-center justify-center gap-2 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 rounded-lg transition-colors">
                <svg v-if="saving" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                </svg>
                {{ submitLabel }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useGlobalApplicationsStore } from 'stores/globalApplications.js'
import ItComponentLogo from 'controllers/vue_components/organizations/it_components/ItComponentLogo.vue'
import CriticalityField from 'controllers/vue_components/organizations/applications/CriticalityField.vue'
import StatusField from 'controllers/vue_components/organizations/applications/StatusField.vue'

const globalApplicationsStore = useGlobalApplicationsStore()

const props = defineProps({
  title: { type: String, required: true },
  submitLabel: { type: String, default: 'Add' },
  saving: { type: Boolean, default: false },
  initialName: { type: String, default: '' },
  initialDescription: { type: String, default: '' },
  initialCriticality: { type: String, default: 'medium' },
  initialStatus: { type: String, default: 'active' },
  error: { type: String, default: '' },
})

const emit = defineEmits(['close', 'submit'])

const NAME_MAX_LENGTH = 40
const DESCRIPTION_MAX_LENGTH = 150

const name = ref(props.initialName)
const description = ref(props.initialDescription)
const criticality = ref(props.initialCriticality)
const status = ref(props.initialStatus)
const nameInput = ref(null)
const showSuggestions = ref(false)
const activeSuggestionIndex = ref(-1)
const suggestionRefs = ref([])

// Freeform typing is always allowed. This is only a helper: if what the user
// typed matches a catalog entry, picking it also prefills the description.
const suggestions = computed(() => {
  const query = name.value.trim().toLowerCase()
  if (query.length < 2) return []
  return globalApplicationsStore.applications
    .filter((app) => app.name.toLowerCase().includes(query))
    .slice(0, 8)
})

// Drives the logo shown inside the Name field itself — recomputed off the
// name field directly (not a separate "selected" flag) so it also lights up
// when editing an existing application whose name already matches the catalog,
// not just right after picking a suggestion. Mirrors AddItComponentSlideOver's matchedComponent.
const matchedApplication = computed(() => {
  const query = name.value.trim().toLowerCase()
  if (!query) return null
  return globalApplicationsStore.applications.find((app) => app.name.toLowerCase() === query) || null
})

watch(suggestions, () => {
  activeSuggestionIndex.value = -1
  suggestionRefs.value = []
})

function setSuggestionRef(el, index) {
  if (el) suggestionRefs.value[index] = el
}

watch(activeSuggestionIndex, (index) => {
  if (index >= 0) suggestionRefs.value[index]?.scrollIntoView({ block: 'nearest' })
})

function selectSuggestion(suggestion) {
  name.value = suggestion.name
  if (!description.value.trim()) description.value = suggestion.description || ''
  showSuggestions.value = false
  activeSuggestionIndex.value = -1
}

function handleNameKeydown(e) {
  const hasOpenSuggestions = showSuggestions.value && suggestions.value.length > 0

  if (hasOpenSuggestions && e.key === 'ArrowDown') {
    e.preventDefault()
    activeSuggestionIndex.value = (activeSuggestionIndex.value + 1) % suggestions.value.length
  } else if (hasOpenSuggestions && e.key === 'ArrowUp') {
    e.preventDefault()
    activeSuggestionIndex.value = activeSuggestionIndex.value <= 0
      ? suggestions.value.length - 1
      : activeSuggestionIndex.value - 1
  } else if (hasOpenSuggestions && e.key === 'Escape') {
    showSuggestions.value = false
    activeSuggestionIndex.value = -1
  } else if (e.key === 'Enter') {
    if (hasOpenSuggestions && activeSuggestionIndex.value >= 0) {
      e.preventDefault()
      selectSuggestion(suggestions.value[activeSuggestionIndex.value])
    } else {
      submit()
    }
  }
}

function handleOutsideClick(e) {
  if (!e.target.closest('.name-suggestions')) showSuggestions.value = false
}

function submit() {
  if (!name.value.trim()) return

  emit('submit', {
    name: name.value.trim(),
    description: description.value.trim(),
    criticality: criticality.value,
    status: status.value,
  })
}

onMounted(() => {
  nextTick(() => nameInput.value?.focus())
  globalApplicationsStore.fetchIfNeeded()
  document.addEventListener('click', handleOutsideClick)
})

onUnmounted(() => {
  document.removeEventListener('click', handleOutsideClick)
})
</script>

<style scoped>
.modal-enter-active { transition: opacity 0.2s ease; }
.modal-leave-active { transition: opacity 0.15s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
.modal-enter-active .animate-in { transition: transform 0.2s ease; }
.modal-enter-from .animate-in { transform: scale(0.96) translateY(8px); }

.gradient-panel {
  background: linear-gradient(155deg, #1E40AF 0%, #2563EB 45%, #1D4ED8 100%);
  background-size: 200% 200%;
  animation: gradientShift 10s ease infinite;
}

@keyframes gradientShift {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

.blob {
  position: absolute;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.10);
  filter: blur(36px);
  pointer-events: none;
}

.blob-1 {
  width: 200px;
  height: 200px;
  top: -50px;
  right: -60px;
  animation: float1 9s ease-in-out infinite;
}

.blob-2 {
  width: 150px;
  height: 150px;
  bottom: -40px;
  left: -30px;
  animation: float2 11s ease-in-out infinite;
}

@keyframes float1 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(-14px, 16px) scale(1.1); }
}

@keyframes float2 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(12px, -14px) scale(1.12); }
}
</style>
